import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Scenario: Nadia — Postpartum Mood & Estrogen Sensitivity

export const postpartumNadia: Scenario = {
  slug: 'postpartum-nadia',
  series: 'Hormones & Mood',
  title: "She prepared for postpartum depression. This still felt different.",
  socialHook:
    "Nadia, 34, knew postpartum depression could happen. She told her clinician early, had support, and still fell into a darkness that felt nothing like her first baby. Her genetics helped explain why the same postpartum hormone crash hit her brain so much harder.",
  meta: {
    description:
      "A fictional case study: Nadia's postpartum depression after her second baby was far more severe than after her first. Her COMT, ESR1, and MTHFR genetics help explain why the postpartum estrogen drop, low ferritin, low folate, and low vitamin D left her only partly helped by an SSRI.",
  },

  persona: {
    name: 'Nadia',
    age: 34,
    sex: 'Female',
    ancestry: 'Middle Eastern / Lebanese',
    occupation: 'Architect',
    chiefConcern:
      "Severe postpartum depression after her second baby, now 4 months old. Her first pregnancy brought mild low mood that resolved by 3 months. This time is different — she can't feel joy, feels overwhelmed, has intrusive thoughts, and is struggling to bond. An SSRI started 6 weeks ago has helped only partially.",
    familyHistory:
      'Mother: severe postnatal depression. Maternal grandmother: depression. No other known psychiatric conditions.',
  },

  clinicalPicture: {
    symptoms: [
      "Unable to feel joy or pleasure — the baby she desperately wanted feels unreachable",
      'Intrusive thoughts — frightening and unwanted',
      'Fatigue that goes far beyond broken sleep',
      'Partial response to antidepressants after six weeks',
    ],
    labs: [
      { name: 'Ferritin', value: '22 µg/L', refRange: '30–200 µg/L', flag: 'low' },
      { name: 'Folate', value: '3.1 ng/mL', refRange: '3.9–26.5 ng/mL', flag: 'low' },
      { name: 'Vitamin D', value: '15 ng/mL', refRange: '20–50 ng/mL', flag: 'low' },
    ],
    medications: ['Sertraline (SSRI), started 6 weeks postpartum — partial response'],
    supplements: ['None currently'],
    lifestyle: [
      'Breastfeeding — 4-month-old second baby',
      'Broken, fragmented sleep',
      'Limited outdoor time since birth',
      'Strong social support from partner; feels unable to communicate the severity to family',
    ],
  },

  genetics: [
    {
      gene: 'COMT',
      variant: 'Val158Met',
      rsid: 'rs4680',
      genotype: 'Val/Val ("warrior" variant)',
      plainMeaning:
      "Nadia could not hold onto joy, even when she wanted to. One reason may be that her brain clears dopamine quickly, and estrogen normally slows that process down. After birth, that brake disappeared.",
      technicalDetail:
        "COMT is the enzyme that clears dopamine from the prefrontal cortex. Val/Val means COMT runs fast — dopamine is cleared quickly. In stable conditions this is an advantage: Val/Val carriers handle acute stress calmly. But estrogen normally slows COMT down, keeping dopamine available for longer. When estrogen crashes postpartum, that brake is removed. COMT runs unchecked: dopamine is swept away before it can sustain motivation, reward, or the feeling of attachment.",
    },
    {
      gene: 'ESR1',
      variant: 'rs2234693 / rs9340799',
      rsid: 'rs2234693, rs9340799',
      genotype: 'High-sensitivity variant',
      plainMeaning:
      "The hormone drop after birth may feel louder in her nervous system than it does for many women. Her brain appears more sensitive to changes in estrogen.",
      technicalDetail:
        "ESR1 encodes estrogen receptor alpha — the protein that responds when estrogen arrives in tissues, including the brain. Her high-sensitivity variant means the receptor is more attuned to changes in estrogen levels. The postpartum drop that every new mother experiences is registered more intensely in her brain. The same absolute fall in estrogen produces a larger withdrawal signal than it would for most women.",
    },
    {
      gene: 'MTHFR',
      variant: 'C677T heterozygous',
      rsid: 'rs1801133',
      genotype: 'C/T (one copy)',
      plainMeaning:
      "Her brain needed raw materials for mood chemistry, and the supply was running low. This variant makes folate activation less efficient, while her folate level was already below range.",
      technicalDetail:
        "MTHFR converts dietary folate into 5-MTHF — the active form used in neurotransmitter synthesis. One copy of C677T reduces that conversion by roughly 35%. Her borderline-low folate compounds this: the supply chain for serotonin and dopamine is running below capacity at exactly the moment her brain needs it most. Standard folate supplements may not bypass this efficiently; methylfolate (5-MTHF) does.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She knew it might happen. She still wasn't ready.",
      body: "Nadia had done her reading. After her first baby, she had a few dark weeks: low mood, tearfulness, a feeling of being underwater. It passed by three months, and she filed it away as 'the baby blues, but a bit worse.' When she became pregnant again, she told her clinician early. She had a plan and support. What came after her second birth was completely different. By week six she could not feel joy for the baby she had desperately wanted. She had frightening intrusive thoughts she did not want. Sertraline took the edge off the darkest moments, but the flat, hollow feeling remained. She wanted to know why this time was so much worse.",
    },
    {
      type: 'narrative',
      heading: "The hormone crash was the first clue",
      body: "During pregnancy, estrogen climbs to the highest levels it will ever reach in a woman's life. It helps support brain circuits for reward, motivation, and attachment. Then, within 72 hours of birth, it falls from its peak to near-zero. That shift can be hard for any new mother. For someone whose dopamine system and estrogen receptors are especially tied to that signal, the drop can feel much more severe.",
    },
    {
      type: 'reveal',
      heading: "Her brain may hear the estrogen drop more loudly",
      body: "Nadia's estrogen was not necessarily lower than other new mothers'. The difference is how her brain may read the change. Her ESR1 result points to higher sensitivity to estrogen shifts, so the same postpartum withdrawal can land with more force. That helps explain why her second experience felt so much darker than her first: the crash was familiar, but her nervous system had less buffer this time.",
    },
    {
      type: 'reveal',
      heading: "The joy signal could not last long enough",
      body: "The most painful part was not that Nadia did not care. It was that warmth and joy would not stay. Her COMT Val/Val result means dopamine can be cleared quickly in the prefrontal cortex. Estrogen normally slows COMT down, giving dopamine more time to support motivation, reward, and connection. After birth, estrogen crashed and that brake was gone. The result was not a character failing. It was a mechanism that made the hollow feeling make sense.",
    },
    {
      type: 'action',
      heading: "What Nadia brings to her next appointment",
      items: [
        "Discuss low-dose transdermal estrogen with her psychiatrist. There is evidence for estrogen as an adjunct to SSRIs in postpartum depression, especially in estrogen-sensitive presentations like Nadia's. This is not a general recommendation for all postpartum depression; her psychiatrist should weigh benefits against breastfeeding status and her individual history.",
        "Address iron urgently. Her ferritin is well below the level needed to support healthy dopamine production. Iron deficiency can worsen fatigue, cognitive slowing, and low mood, and it can limit the benefit of other mood interventions.",
        "Switch to active methylfolate and add vitamin D. Her MTHFR variant means standard folate may not convert efficiently, while 5-MTHF bypasses that step. Her vitamin D is also low, which can independently affect mood regulation.",
        "Review whether the SSRI dose and fit are right. If sertraline has only partly helped, her psychiatrist can consider whether the dose needs adjusting or whether a different medication profile makes more sense.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "The factors layering onto Nadia's postpartum picture",
      caption:
        "Nadia's genetic picture helps explain why recovery has been slower than expected and why each intervention targets a specific mechanism, not just general mood support.",
      baselineLabel: 'Postpartum week 4 — before any intervention',
      baseline: 85,
      levers: [
        {
          label: 'Estrogen patch (with psychiatrist)',
          delta: -20,
          description:
            "Low-dose transdermal estrogen may restore the signal that COMT and ESR1 depend on, slowing dopamine clearance and softening withdrawal sensitivity. This is the most targeted lever for her specific genetic profile.",
        },
        {
          label: 'Iron supplementation / infusion',
          delta: -12,
          description:
            "Low ferritin is actively impairing dopamine synthesis and worsening fatigue. Repletion is a direct neurochemical intervention, not just a general health measure.",
        },
        {
          label: 'Methylfolate + vitamin D',
          delta: -8,
          description:
            "MTHFR C677T plus low folate means the supply chain for serotonin and dopamine is restricted. Active methylfolate and vitamin D restore upstream capacity for neurotransmitter synthesis.",
        },
        {
          label: 'SSRI dose optimization',
          delta: -15,
          description:
            "Sertraline has only partly helped. A dose review or agent switch, informed by her broader clinical picture, may convert a partial response into a more meaningful one.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'postpartum-nadia',
    label: 'Explore your hormones and mood genetics in Coach',
  },
}
